import Vue from "vue";

import { MicData } from "@joyz/app-core/models/microphone";

import {
  ApiConversationProblem,
  AppChatConversationUserAnswer,
  ChatState,
  ConversationProblemsState,
  ConversationProblemsDisplay,
  ExerciseState,
  IsChatProblemsHandler,
  PayloadExerciseConversationAnswer
} from "@tt-app/models/exercise";
import { api } from "@tt-app/api";

import { BaseHandler, exerciseHandler } from "../../common";
import { buildRespTime, buildChatAnswerPayload } from "../api";
import { getConversationProblemDisplay } from "./misc";

class ChatProblemsHandler extends BaseHandler implements IsChatProblemsHandler {
  // --------------------------------------------------------------------------
  //  Initialisation
  // --------------------------------------------------------------------------
  constructor() {
    super();
  }

  // --------------------------------------------------------------------------
  //  Methods
  // --------------------------------------------------------------------------
  getNewState(): ConversationProblemsState {
    return {
      current: undefined,
      problems: [],
      responseTime: {
        botEndTime: undefined,
        userStartTime: undefined
      },
      results: [],
      retryCount: -1,
      display: {
        inQueue: [],
        hints: []
      }
    };
  }

  loadProblemsData(
    state: ConversationProblemsState,
    chatState: ChatState
  ): ConversationProblemsState {
    let problems: ApiConversationProblem[] = [];
    if (chatState.data) {
      const msgs = chatState.data.exercise.pages[0].body;
      problems = msgs.filter((m): m is ApiConversationProblem => m.type === "CHAT_PROBLEM");
    }

    if (!problems.length) {
      // Do something: there is no problems!
    }

    return {
      ...state,
      problems
    };
  }

  clearQueue(state: ConversationProblemsState): ConversationProblemsState {
    return {
      ...state,
      display: {
        ...state.display,
        inQueue: []
      }
    };
  }

  loadProblem(state: ConversationProblemsState, problemUuid?: string): ConversationProblemsState {
    // Data checker
    if (!state.problems) {
      this._warn("ChatProblems.loadProblem: No Chat data!!!");
      return state;
    }

    // Find the problem to start
    const findPb = (p: ApiConversationProblem): boolean =>
      problemUuid ? p.problemUuid === problemUuid : p.location === "start";
    const nextPb = state.problems.find(findPb);

    if (!nextPb) {
      this._warn(`ChatProblems.loadProblem: no problem found for problemUuid ${problemUuid}`);
      return state;
    }

    // Archive results if necessary
    let results = state.results;
    if (state.current) {
      results = [...results, { uuid: state.current.problemUuid, retryCount: state.retryCount }];
    }
    this._debug(`ChatProblems.loadProblem: ${JSON.stringify(nextPb.attempts[0].components[0])}`);

    return {
      ...state,
      results,
      current: nextPb,
      retryCount: 0,
      display: getConversationProblemDisplay(nextPb)
    };
  }

  onBotMessageFinished(state: ConversationProblemsState): ConversationProblemsState {
    this._debug(`ChatProblems.onBotMessageFinished`);

    return {
      ...state,
      responseTime: {
        ...state.responseTime,
        botEndTime: performance.now()
      }
    };
  }

  onUserAnswerStarted(state: ConversationProblemsState): ConversationProblemsState {
    this._debug(`ChatProblems.onUserAnswerStarted`);

    return {
      ...state,
      responseTime: {
        ...state.responseTime,
        userStartTime: performance.now()
      }
    };
  }

  async onUserAnswerSent<V extends Vue>(
    component: V,
    state: ConversationProblemsState,
    exerciseState: ExerciseState,
    micData: MicData[]
  ): Promise<ConversationProblemsState> {
    this._debug(`ChatProblems.onUserAnswerSent data: ${JSON.stringify(micData)}`);

    // Data check
    if (micData.length === 0 || micData.every(d => d.recognition.value === "")) {
      this._warn(`ChatProblems.onUserAnswerSent: empty micData: ${JSON.stringify(micData)}`);
      return state;
    }
    if (!state.current) {
      this._warn(`ChatProblems.onUserAnswerSent: empty current: ${JSON.stringify(state)}`);
      return state;
    }

    // Build answer
    const authInfo = await exerciseHandler.getAuthExerciseInfo(component, exerciseState);
    const respTime = buildRespTime(state.responseTime, micData);
    const payload: PayloadExerciseConversationAnswer = buildChatAnswerPayload(
      state,
      authInfo,
      exerciseState.logId,
      respTime,
      micData
    );

    // Submit answer
    const chatResp = await api.exercise.submitConversationAnswer(payload);
    this._info(`ChatProblems.onUserAnswerSent: isCorrect: ${chatResp.isCorrect}`);

    // Build user answer
    const userMsg: AppChatConversationUserAnswer = {
      type: "CHAT_CONVERSATION_USER_ANSWER",
      side: "right",
      avatar: "",
      text: micData.map(d => d.recognition.display),
      audioUrls: micData
        .map(d => d.recording.url)
        .filter((url): url is string => url !== undefined),
      autoPlay: false,
      isCorrect: chatResp.isCorrect
    };

    // Prepare variables for next step
    let results = state.results;
    let current: ApiConversationProblem | undefined = state.current;
    let retryCount = state.retryCount;
    let display: ConversationProblemsDisplay = { inQueue: [userMsg], hints: [] };

    // Optional: Try again messages
    if (!chatResp.isCorrect) {
      retryCount++;
      const retryDisplay = getConversationProblemDisplay(current, retryCount);
      display = {
        inQueue: [...display.inQueue, ...retryDisplay.inQueue],
        hints: retryDisplay.hints
      };
    }

    // Optional: move forward to next problem
    if (chatResp.transition) {
      const { nextProblemUuid } = chatResp;
      const nextProblemState = this.loadProblem(state, nextProblemUuid);
      this._info(`ChatProblems.onUserAnswerSent: transitioning to ${nextProblemUuid}`);

      results = nextProblemState.results;
      retryCount = nextProblemState.retryCount;
      current = nextProblemState.current;
      display = {
        inQueue: [...display.inQueue, ...nextProblemState.display.inQueue],
        hints: nextProblemState.display.hints
      };
    }

    return { ...state, results, current, retryCount, display };
  }
}

export const chatProblemsHandler = new ChatProblemsHandler();
